import { useState } from 'react';

interface DayData {
  date: string;
  percentage: number;
  actual: number;
  goal: number;
}

interface NutritionHeatMapProps {
  data: DayData[];
  macroType: 'overall' | 'protein' | 'carbs' | 'fat';
  year: number;
  month: number;
}

const getColorForMacro = (macroType: string, percentage: number): string => {
  if (percentage === 0) return 'bg-gray-100';

  const colorSchemes = {
    overall: {
      low: 'bg-green-100',
      medium: 'bg-green-300',
      high: 'bg-green-500',
      full: 'bg-green-600',
      over: 'bg-green-700',
    },
    protein: {
      low: 'bg-blue-100',
      medium: 'bg-blue-300',
      high: 'bg-blue-500',
      full: 'bg-blue-600',
      over: 'bg-blue-700',
    },
    carbs: {
      low: 'bg-orange-100',
      medium: 'bg-orange-300',
      high: 'bg-orange-500',
      full: 'bg-orange-600',
      over: 'bg-orange-700',
    },
    fat: {
      low: 'bg-yellow-100',
      medium: 'bg-yellow-300',
      high: 'bg-yellow-500',
      full: 'bg-yellow-600',
      over: 'bg-amber-700',
    },
  };

  const scheme = colorSchemes[macroType];

  if (percentage < 50) return scheme.low;
  if (percentage < 75) return scheme.medium;
  if (percentage < 100) return scheme.high;
  if (percentage < 125) return scheme.full;
  return scheme.over;
};

export default function NutritionHeatMap({ data, macroType, year, month }: NutritionHeatMapProps) {
  const [hoveredDay, setHoveredDay] = useState<DayData | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const buildMonthCalendar = () => {
    const dataMap = new Map<string, DayData>();
    data.forEach((day) => {
      dataMap.set(day.date, day);
    });

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startDayOfWeek = firstDayOfMonth.getDay();

    const calendarDays: (DayData | null)[] = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      calendarDays.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = dataMap.get(dateStr);

      if (dayData) {
        calendarDays.push(dayData);
      } else {
        calendarDays.push({
          date: dateStr,
          percentage: 0,
          actual: 0,
          goal: 0,
        });
      }
    }

    const weeks: (DayData | null)[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return weeks;
  };

  const weeks = buildMonthCalendar();

  const handleMouseEnter = (day: DayData, event: React.MouseEvent<HTMLButtonElement>) => {
    setHoveredDay(day);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleMouseLeave = () => {
    setHoveredDay(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDayNumber = (dateString: string): number => {
    const date = new Date(dateString);
    return date.getDate();
  };

  const macroLabel =
    macroType === 'overall' ? 'Food Logged' :
    macroType === 'protein' ? 'Protein' :
    macroType === 'carbs' ? 'Carbs' :
    'Fat';

  const getUnit = () => macroType === 'overall' ? '' : 'g';

  return (
    <div className="relative">
      <div className="space-y-1.5">
        <div className="grid grid-cols-7 gap-1.5 mb-2">
          {dayLabels.map((label, index) => (
            <div key={index} className="text-center text-[10px] font-bold text-gray-400">
              {label}
            </div>
          ))}
        </div>

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-1.5">
            {week.map((day, dayIndex) => {
              if (!day) {
                return <div key={dayIndex} className="w-full aspect-square" />;
              }

              return (
                <button
                  key={dayIndex}
                  className={`w-full aspect-square rounded-md ${getColorForMacro(macroType, day.percentage)} transition-all hover:scale-105 hover:shadow-md relative flex items-center justify-center`}
                  onMouseEnter={(e) => handleMouseEnter(day, e)}
                  onMouseLeave={handleMouseLeave}
                  aria-label={`${formatDate(day.date)}: ${day.actual}${getUnit()} / ${day.goal}${getUnit()} (${day.percentage.toFixed(0)}%)`}
                >
                  <span className="text-[9px] font-bold text-gray-700">
                    {getDayNumber(day.date)}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {hoveredDay && (
        <div
          className="fixed z-50 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          <div className="font-bold mb-1">{formatDate(hoveredDay.date)}</div>
          <div className="text-gray-300">
            {macroType === 'overall' 
              ? (hoveredDay.percentage > 0 ? '✅ Food logged' : '❌ No food logged')
              : `${macroLabel}: ${hoveredDay.actual.toFixed(1)}${getUnit()} / ${hoveredDay.goal.toFixed(1)}${getUnit()}`
            }
          </div>
          {macroType !== 'overall' && (
            <div className="font-semibold text-cyan-400">
              {hoveredDay.percentage.toFixed(0)}% achieved
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-3 justify-center">
        {macroType === 'overall' ? (
          <>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-gray-100"></div>
              <span className="text-[9px] text-gray-500 font-medium">No log</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-green-500"></div>
              <span className="text-[9px] text-gray-500 font-medium">Logged</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded ${getColorForMacro(macroType, 25)}`}></div>
              <span className="text-[9px] text-gray-500 font-medium">0-50%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded ${getColorForMacro(macroType, 75)}`}></div>
              <span className="text-[9px] text-gray-500 font-medium">51-99%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded ${getColorForMacro(macroType, 100)}`}></div>
              <span className="text-[9px] text-gray-500 font-medium">100%+</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
